import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { API_URL } from '../lib/api';
import { toast } from 'sonner';

import {
    Building2,
    KeyRound,
    Webhook,
    Plus,
    Trash2,
    Copy,
    Check,
    Loader2,
    AlertCircle
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { format } from 'date-fns';
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useTranslation } from '../i18n';

// API URL Base - used for the Docs link only
// All actual API calls go through the shared `api` library

function RevealableKey({ keyId, maskedKey }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = React.useState(false);
  const [fullKey, setFullKey] = React.useState(null);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const handleReveal = async () => {
    try {
      const res = await api.get(`/developers/api-keys/${keyId}/reveal`);
      setFullKey(res.key);
      setRevealed(true);
      setShowConfirm(false);
      setAccepted(false);
    } catch { toast.error("Failed to reveal key"); }
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(fullKey || maskedKey);
    toast.success("Copied to clipboard");
  };
  return (
    <>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm select-all">{revealed ? fullKey : maskedKey}</span>
        {!revealed && <button onClick={() => setShowConfirm(true)} className="text-xs text-primary hover:underline">{t("developersettings.reveal")}</button>}
        {revealed && <button onClick={handleCopy} className="text-xs text-primary hover:underline">{t("developersettings.copy")}</button>}
        {revealed && <button onClick={() => { setRevealed(false); setFullKey(null); }} className="text-xs text-muted-foreground hover:underline">{t("developersettings.hide")}</button>}
      </div>
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowConfirm(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              <h3 className="text-lg font-bold">{t("developersettings.securityWarning")}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">This API key grants <strong>full access</strong> to your organization's data including deals, contracts, documents, and client information.</p>
            <ul className="text-sm text-muted-foreground mb-4 space-y-1.5 list-disc pl-5">
              <li>{t("developersettings.neverShareThisKey")}</li>
              <li>{t("developersettings.anyoneWithThisKey")}</li>
              <li>{t("developersettings.ifCompromisedRevokeImmediately")}</li>
            </ul>
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} className="mt-0.5 rounded" />
              <span className="text-sm font-medium">{t("developersettings.iUnderstandAndAccept")}</span>
            </label>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowConfirm(false); setAccepted(false); }} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800">{t("developersettings.cancel")}</button>
              <button onClick={handleReveal} disabled={!accepted} className={"px-4 py-2 text-sm rounded-lg font-medium text-white " + (accepted ? "bg-amber-500 hover:bg-amber-600" : "bg-gray-300 cursor-not-allowed")}>{t("developersettings.revealKey")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function DeveloperSettingsPage() {
  const { t } = useTranslation();
    const { user } = useAuth();
    const queryClient = useQueryClient();

    // API Keys state
    const [isApiKeyDialogOpen, setIsApiKeyDialogOpen] = useState(false);
    const [newApiKeyName, setNewApiKeyName] = useState('');
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [copiedKey, setCopiedKey] = useState(false);

    // Webhooks state
    const [isWebhookDialogOpen, setIsWebhookDialogOpen] = useState(false);
    const [newWebhookUrl, setNewWebhookUrl] = useState('');
    const [newWebhookEvents, setNewWebhookEvents] = useState('*');
    const [newWebhookDesc, setNewWebhookDesc] = useState('');

    // ------------------------------------------------------------------
    // Queries
    // ------------------------------------------------------------------
    const { data: apiKeys, isLoading: loadingKeys } = useQuery({
        queryKey: ['api-keys', user?.organization_id],
        queryFn: () => api.get('/developers/api-keys'),
        enabled: !!user?.organization_id
    });

    const { data: webhooks, isLoading: loadingWebhooks } = useQuery({
        queryKey: ['webhooks', user?.organization_id],
        queryFn: () => api.get('/developers/webhooks'),
        enabled: !!user?.organization_id
    });

    // ------------------------------------------------------------------
    // Mutations
    // ------------------------------------------------------------------
    const createApiKey = useMutation({
        mutationFn: (name) => api.post('/developers/api-keys', { name }),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['api-keys']);
            setNewlyCreatedKey(data.key); // The only time the full key is shown
            setNewApiKeyName('');
            toast.success('API Key created successfully');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create API Key');
        }
    });

    const revokeApiKey = useMutation({
        mutationFn: (id) => api.delete(`/developers/api-keys/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['api-keys']);
            toast.success('API Key revoked');
        }
    });

    const createWebhook = useMutation({
        mutationFn: (payload) => api.post('/developers/webhooks', payload),
        onSuccess: () => {
            queryClient.invalidateQueries(['webhooks']);
            setIsWebhookDialogOpen(false);
            setNewWebhookUrl('');
            setNewWebhookDesc('');
            toast.success('Webhook registered successfully');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to register webhook');
        }
    });

    const deleteWebhook = useMutation({
        mutationFn: (id) => api.delete(`/developers/webhooks/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['webhooks']);
            toast.success('Webhook deleted');
        }
    });

    // ------------------------------------------------------------------
    // Handlers
    // ------------------------------------------------------------------
    const handleCopyKey = async () => {
        if (newlyCreatedKey) {
            await navigator.clipboard.writeText(newlyCreatedKey);
            setCopiedKey(true);
            setTimeout(() => setCopiedKey(false), 2000);
        }
    };

    const handleCreateWebhook = (e) => {
        e.preventDefault();
        if (!newWebhookUrl) return;
        createWebhook.mutate({
            url: newWebhookUrl,
            events: newWebhookEvents,
            description: newWebhookDesc
        });
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center p-12">
                <Alert variant="destructive" className="max-w-md">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{t("developersettings.accessDenied")}</AlertTitle>
                    <AlertDescription>{t("developersettings.onlyBusinessAccountsHave")}</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="flex-1 space-y-4 p-4 md:p-8 pt-6 max-w-7xl mx-auto w-full pb-20">
            <div className="flex items-center justify-between space-y-2">
                <div>
                    <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                        <p className="text-sm text-amber-800 dark:text-amber-200">API keys provide programmatic access to your organization's data. Treat them like passwords — never share publicly.</p>
                    </div>
                    <h2 className="text-3xl font-bold tracking-tight">{t("developersettings.developerOverview")}</h2>
                    <p className="text-muted-foreground">{t("developersettings.manageYourApiKeys")}</p>
                </div>
            </div>

            <Tabs defaultValue="api-keys" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="api-keys" className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        API Keys
                    </TabsTrigger>
                    <TabsTrigger value="webhooks" className="flex items-center gap-2">
                        <Webhook className="h-4 w-4" />
                        Webhooks
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="api-keys" className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border rounded-lg p-4 sm:p-6 bg-card">
                        <div>
                            <h3 className="text-lg font-medium">{t("developersettings.secretApiKeys")}</h3>
                            <p className="text-sm text-muted-foreground max-w-[600px]">
                                These keys will allow you to authenticate API requests to the PocketFiler platform.
                                Keep them secure. Do not share them in publicly accessible areas such as GitHub or client-side code.
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                For endpoints and payloads, read the <a href={`${API_URL}/docs`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">{t("developersettings.interactiveApiDocumentationRarr")}</a>
                            </p>
                        </div>
                        <Button className="w-full sm:w-auto" onClick={() => {
                            setNewlyCreatedKey(null);
                            setNewApiKeyName('');
                            setIsApiKeyDialogOpen(true);
                        }} disabled={createApiKey.isPending}>
                            <Plus className="mr-2 h-4 w-4" /> Generate New Key
                        </Button>
                    </div>

                    <div className="border rounded-lg bg-card overflow-x-auto w-full">
                        <Table className="min-w-[800px]">
                            <TableHeader className="bg-muted/50">
                                <TableRow>
                                    <TableHead>{t("developersettings.name")}</TableHead>
                                    <TableHead>{t("developersettings.key")}</TableHead>
                                    <TableHead>{t("developersettings.createdAt")}</TableHead>
                                    <TableHead>{t("developersettings.status")}</TableHead>
                                    <TableHead className="text-right">{t("developersettings.actions")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loadingKeys ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                                        </TableCell>
                                    </TableRow>
                                ) : !apiKeys || apiKeys.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            No API keys generated yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    apiKeys.map((key) => (
                                        <TableRow key={key._id} className={!key.is_active ? "opacity-50" : ""}>
                                            <TableCell className="font-medium">{key.name}</TableCell>
                                            <TableCell className="font-mono text-sm"><RevealableKey keyId={key._id} maskedKey={key.key} /></TableCell>
                                            <TableCell>{format(new Date(key.created_at), 'MMM dd, yyyy')}</TableCell>
                                            <TableCell>
                                                {key.is_active ? (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-200">
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900 dark:text-red-200">
                                                        Revoked
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {key.is_active && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => {
                                                            if (window.confirm("Are you sure you want to revoke this key? Any systems using it will immediately lose access.")) {
                                                                revokeApiKey.mutate(key._id);
                                                            }
                                                        }}
                                                        disabled={revokeApiKey.isPending}
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-1" /> Revoke
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>

                <TabsContent value="webhooks" className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border rounded-lg p-4 sm:p-6 bg-card">
                        <div>
                            <h3 className="text-lg font-medium">{t("developersettings.webhookEndpoints")}</h3>
                            <p className="text-sm text-muted-foreground max-w-[600px]">
                                Listen to events happening in your Organization in real-time. PocketFiler will send HTTP POST requests to your configured URLs.
                            </p>
                        </div>
                        <Button className="w-full sm:w-auto" onClick={() => setIsWebhookDialogOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" /> Add Endpoint
                        </Button>
                    </div>

                    <div className="border rounded-lg bg-card overflow-x-auto w-full">
                        <Table className="min-w-[700px]">
                            <TableHeader className="bg-muted/50">
                                <TableRow>
                                    <TableHead>{t("developersettings.url")}</TableHead>
                                    <TableHead>{t("developersettings.events")}</TableHead>
                                    <TableHead>{t("developersettings.description")}</TableHead>
                                    <TableHead className="text-right">{t("developersettings.actions")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loadingWebhooks ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                                        </TableCell>
                                    </TableRow>
                                ) : !webhooks || webhooks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                            No webhook endpoints configured.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    webhooks.map((hook) => (
                                        <TableRow key={hook._id}>
                                            <TableCell className="font-mono text-sm max-w-[300px] truncate" title={hook.url}>
                                                {hook.url}
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                    {hook.events}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">{hook.description || '-'}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    onClick={() => {
                                                        if (window.confirm("Delete this webhook endpoint?")) {
                                                            deleteWebhook.mutate(hook._id);
                                                        }
                                                    }}
                                                    disabled={deleteWebhook.isPending}
                                                >
                                                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>
            </Tabs>

            {/* API Key Modal */}
            <Dialog
                open={isApiKeyDialogOpen}
                onOpenChange={(open) => {
                    if (!open && newlyCreatedKey) {
                        setNewlyCreatedKey(null);
                        setNewApiKeyName('');
                    }
                    setIsApiKeyDialogOpen(open);
                }}
            >
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{newlyCreatedKey ? 'Save Your API Key' : 'Create new API Key'}</DialogTitle>
                        <DialogDescription>
                            {newlyCreatedKey
                                ? 'Please copy this key and save it somewhere safe. For security reasons, we will never show it to you again.'
                                : 'Enter a name to help you identify this key later (e.g., "ERP Integration").'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {newlyCreatedKey ? (
                        <div className="py-6">
                            <div className="flex items-center gap-2 p-3 bg-muted rounded-md border border-border">
                                <code className="text-sm font-mono flex-1 break-all">{newlyCreatedKey}</code>
                                <Button variant="ghost" size="icon" onClick={handleCopyKey}>
                                    {copiedKey ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid gap-4 py-4">
                            <div className="flex flex-col sm:grid sm:grid-cols-4 sm:items-center gap-4">
                                <Label htmlFor="name" className="sm:text-right">
                                    Name
                                </Label>
                                <Input
                                    id="name"
                                    value={newApiKeyName}
                                    onChange={(e) => setNewApiKeyName(e.target.value)}
                                    placeholder="e.g. Zapier Integration"
                                    className="sm:col-span-3"
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {newlyCreatedKey ? (
                            <Button onClick={() => setIsApiKeyDialogOpen(false)}>{t("developersettings.done")}</Button>
                        ) : (
                            <Button
                                onClick={() => createApiKey.mutate(newApiKeyName)}
                                disabled={!newApiKeyName.trim() || createApiKey.isPending}
                            >
                                {createApiKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Create secret key
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Webhook Modal */}
            <Dialog open={isWebhookDialogOpen} onOpenChange={setIsWebhookDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t("developersettings.addWebhookEndpoint")}</DialogTitle>
                        <DialogDescription>
                            We'll send HTTP POST requests to this URL when events occur.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateWebhook}>
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="url">{t("developersettings.payloadUrl")}</Label>
                                <Input
                                    id="url"
                                    type="url"
                                    placeholder="https://api.yourdomain.com/webhook"
                                    value={newWebhookUrl}
                                    onChange={(e) => setNewWebhookUrl(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="events">{t("developersettings.eventsToSend")}</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    value={newWebhookEvents}
                                    onChange={(e) => setNewWebhookEvents(e.target.value)}
                                >
                                    <option value="*">{t("developersettings.sendMeEverything")}</option>
                                    <option value="deal.status_changed">{t("developersettings.dealStatusChangedDealstatuschanged")}</option>
                                    <option value="document.uploaded">{t("developersettings.documentUploadedDocumentuploaded")}</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="desc">{t("developersettings.descriptionOptional")}</Label>
                                <Input
                                    id="desc"
                                    placeholder="Production ERP endpoint"
                                    value={newWebhookDesc}
                                    onChange={(e) => setNewWebhookDesc(e.target.value)}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsWebhookDialogOpen(false)}>{t("developersettings.cancel")}</Button>
                            <Button type="submit" disabled={!newWebhookUrl || createWebhook.isPending}>
                                {createWebhook.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Add Endpoint
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
